import { useMutation } from '@apollo/client/react';
import { Button, Form, Modal, Select } from 'antd';
import { filter, includes, map } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { SEEK_REGISTER_LOG_APPROVAL } from '../../../../../graphql/Mutation';

function SeekApprovalModal(props) {
  const {
    showModal,
    setShowModal,
    registerData,
    logApprovalLevel = 1,
    registerLogId,
    onComplete,
  } = props;
  const [form] = Form.useForm();
  const [selectedAll, setSelectedAll] = useState(false);

  const [seekRegisterLogApproval, { loading }] = useMutation(
    SEEK_REGISTER_LOG_APPROVAL,
    {
      onCompleted: () => {
        setShowModal(false);
        if (onComplete) {
          onComplete();
        }
      },
      onError: () => {
        // Error will be handled globally
      },
    },
  );

  // Filter approvers by matching levelNumber with logApprovalLevel
  const filteredApprovers = useMemo(
    () =>
      filter(
        registerData?.approvers,
        (approver) => approver?.levelNumber === logApprovalLevel,
      ),
    [registerData?.approvers, logApprovalLevel],
  );

  // Prepare options for the dropdown
  const approverOptions = useMemo(
    () =>
      filteredApprovers?.map((approver) => {
        // Determine if this is an internal or external approver
        const isInternal = !!approver?.internalUserApprover;
        const value = isInternal
          ? approver?.internalUserApprover?.id
          : approver?.externalUserApprover?.id;
        const label = isInternal
          ? approver?.internalUserApprover?.name
          : approver?.externalUserApprover?.name;

        return {
          value,
          label,
          approver,
        };
      }) || [],
    [filteredApprovers],
  );

  useEffect(() => {
    // Auto-select if only single approver exists
    if (approverOptions?.length === 1) {
      form.setFieldsValue({
        approverIds: [approverOptions[0]?.value],
      });
    }
  }, [approverOptions, form]);

  useEffect(() => {
    if (selectedAll) {
      form.setFieldsValue({
        approverIds: ['all'],
      });
    }
  }, [selectedAll, form]);

  const handleDeselectAll = () => {
    setSelectedAll(false);
    form.setFieldsValue({
      approverIds: [],
    });
  };

  const onSubmitFinish = async (formValues) => {
    // If "Select All" is selected, send all approver IDs
    const isAllSelected = includes(formValues?.approverIds, 'all');
    const notifyUser = isAllSelected
      ? map(approverOptions, (option) => option?.value)
      : formValues?.approverIds || [];

    await seekRegisterLogApproval({
      variables: {
        registerLogId,
        data: {
          notifyUser,
        },
      },
    });
  };

  return (
    <Modal
      maskClosable={false}
      title="Select Approvers to Notify"
      open={showModal}
      onCancel={() => setShowModal(false)}
      footer={null}
      width={600}
      closeIcon={false}
      centered
      destroyOnHidden
      className="seek-approval-modal"
    >
      <Form form={form} layout="vertical" onFinish={onSubmitFinish}>
        <div className="mb-16">
          <p className="text-secondary">
            Select which approver(s) should receive email notifications for this
            approval request.
          </p>
        </div>

        {
          <Form.Item
            name="approverIds"
            label={
              <div className="d-flex justify-between width-percent-100">
                <span>Approvers</span>
                {selectedAll && (
                  <span
                    className="text-primary pointer"
                    role="button"
                    tabIndex="0"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </span>
                )}
              </div>
            }
            rules={[
              {
                required: true,
                message: 'Please select at least one approver',
                type: 'array',
              },
            ]}
            normalize={(value) => {
              if (value?.includes('all')) {
                setSelectedAll(true);
                return ['all'];
              }
              setSelectedAll(false);
              return value;
            }}
          >
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select Approvers"
              disabled={selectedAll}
              styles={{ popup: { maxHeight: 400, overflow: 'auto' } }}
              filterOption={(input, option) => {
                // Always show "Select All" option
                if (option?.value === 'all') {
                  return true;
                }
                // Filter other options by label
                return option?.label
                  ?.toLowerCase()
                  ?.includes(input?.toLowerCase());
              }}
              options={
                approverOptions?.length > 1
                  ? [
                      {
                        key: 'select-all-option',
                        value: 'all',
                        label: selectedAll ? 'Selected All' : 'Select All',
                      },
                      ...approverOptions,
                    ]
                  : approverOptions
              }
            />
          </Form.Item>
        }

        <div className="form-buttons mt-24">
          <Button
            shape="round"
            onClick={() => setShowModal(false)}
            className="mr-10"
          >
            Cancel
          </Button>
          <Button
            shape="round"
            type="primary"
            htmlType="submit"
            loading={loading}
            className="save-button"
          >
            Send for Approval
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default SeekApprovalModal;
